import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MemoryService } from 'src/app/services/memory.service';
import { ChangelogDialogComponent } from '../dialogs/changelog-dialog/changelog-dialog.component';
import { DialogSupportComponent } from '../dialogs/dialog-support/dialog-support.component';
import { TranslocoModule } from '@jsverse/transloco';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { NotificationDropdownComponent } from './notification-dropdown/notification-dropdown.component';
import { UserAvatarComponent } from './user-avatar/user-avatar.component';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { BackendService } from 'src/app/services/backend.service';
import { JwtService } from 'src/app/services/jwt.service';
import { DecodedJwt } from 'src/app/services/token.interface';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CapabilityCheckPipe } from 'src/app/pipes/check-capability.pipe';
import { TranslationBackendService } from 'src/app/services/translation-file.service';
import { TranslationKeeperService } from 'src/app/services/translation-keeper.service';
import { UserNotificationService } from 'src/app/services/user-notification.service';
import { environment } from 'src/environments/environment';
import { ProcessHandlerService } from 'src/app/process-engine/process/process-handler-dialog/process-handler.service';
import { filter, Subscription } from 'rxjs';
import { HrAuthService } from 'src/app/services/hr-auth.service';
import { HrSessionService } from 'src/app/services/hr-session.service';
import { formatElapsedDuration } from 'src/app/shared/utils';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    TranslocoModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    RouterModule,
    UserAvatarComponent,
    NotificationDropdownComponent,
    CapabilityCheckPipe
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() isMobile = false;
  @Input() navbarOpen = true;
  @Output() readonly navbarToggled = new EventEmitter<boolean>();

  languages: { label: string; value: string }[] = [];
  roleId: number;
  isLoggedIn = false;
  isPersonnelRoute = false;
  hrJwtRemainingText: string | null = null;
  hrJwtRemainingLow = false;
  appMode = ['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'local' : environment.environment.includes('staging') ? 'staging' : 'production';

  private routerSubscription?: Subscription;
  private hrJwtTimer?: ReturnType<typeof setInterval>;

  constructor(
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    public readonly memoryService: MemoryService,
    private readonly router: Router,
    public readonly jwtService: JwtService,
    private readonly backendService: BackendService,
    private readonly translationBackendService: TranslationBackendService,
    private readonly translationKeeper: TranslationKeeperService,
    private readonly processHandlerService: ProcessHandlerService,
    private readonly userNotificationService: UserNotificationService,
    private readonly hrAuthService: HrAuthService,
    private readonly hrSessionService: HrSessionService,
    private readonly destroyRef: DestroyRef
  ) {
    this.translationKeeper.languageOptions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((options) => {
      this.languages = options;
      this.cd.markForCheck();
    });
  }

  ngOnInit(): void {
    this.jwtService.$loginChange.subscribe(({ userdata }) => this.onLoginChanged(userdata));
    if (this.jwtService.isLoggedIn) this.onLoginChanged(this.jwtService.userData);

    this.checkPersonnelRoute();
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkPersonnelRoute();
      });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.clearHrJwtTimer();
  }

  private checkPersonnelRoute(): void {
    const isPersonnel = this.router.url.startsWith('/personnel');
    if (this.isPersonnelRoute !== isPersonnel) {
      this.isPersonnelRoute = isPersonnel;
      this.syncHrJwtTimer();
    }
  }

  private syncHrJwtTimer(): void {
    if (!this.isPersonnelRoute) {
      this.clearHrJwtTimer();
      this.hrJwtRemainingText = null;
      this.hrJwtRemainingLow = false;
      return;
    }

    this.updateHrJwtRemaining();
    if (!this.hrJwtTimer) {
      this.hrJwtTimer = setInterval(() => this.updateHrJwtRemaining(), 1000);
    }
  }

  /**
   * Shows the time left of the HR elevation. Every elevated request extends it, also one from
   * another tab, so the countdown can go up again. When it runs out it just disappears: the MFA
   * is asked for again only when an action needs it, and only in that tab.
   */
  private updateHrJwtRemaining(): void {
    const remainingMs = this.hrSessionService.isElevationDialogOpen ? null : this.hrAuthService.getElevationRemainingMs();

    if (remainingMs !== null) {
      this.hrJwtRemainingText = formatElapsedDuration(Math.ceil(remainingMs / 1000));
      this.hrJwtRemainingLow = remainingMs < 2 * 60 * 1000;
    } else {
      this.hrJwtRemainingText = null;
      this.hrJwtRemainingLow = false;
    }

    this.cd.markForCheck();
  }

  private clearHrJwtTimer(): void {
    if (this.hrJwtTimer) {
      clearInterval(this.hrJwtTimer);
      this.hrJwtTimer = undefined;
    }
  }

  onLoginChanged(userData: DecodedJwt) {
    this.isLoggedIn = userData !== null;
    if (!this.isLoggedIn) return;
    this.roleId = userData.role.id;
    this.userNotificationService.loadNotifications();
  }

  reload() {
    window.location.reload();
  }

  onSupportClicked(): void {
    const dimensions = this.processHandlerService.getDialogScreenSize();
    this.dialog.open(DialogSupportComponent, {
      disableClose: true,
      ...dimensions
    });
  }

  openChangelogDialog(): void {
    const dimensions = this.processHandlerService.getDialogScreenSize();
    this.dialog.open(ChangelogDialogComponent, {
      ...dimensions,
      autoFocus: 'dialog'
    });
  }

  mimicReset() {
    this.backendService.post<'admin/mimicReset'>('admin/mimic/reset').subscribe((data) => {
      this.jwtService.token = data.token;
      this.router.navigateByUrl(this.memoryService.mimicReturnUrl || '/user/list');
    });
  }

  translateNow() {
    this.translationBackendService.translateAutomatically();
  }

  get helpLink(): string {
    return this.isLoggedIn ? '/support/help' : '/auth/help';
  }
}
